import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth, User } from '../context/AuthContext'; // Import User type

const LinkAccountPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { processOAuthLogin } = useAuth(); // For setting user and tokens after successful link

  const [linkingToken, setLinkingToken] = useState<string | null>(null);
  const [provider, setProvider] = useState<string | null>(null);
  const [existingEmail, setExistingEmail] = useState<string | null>(null);
  const [oauthName, setOauthName] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const prov = params.get('provider');
    const email = params.get('email'); // Existing account's email
    const name = params.get('name');   // Name from the new OAuth provider

    if (!token || !prov || !email) {
      setError('Account linking information is incomplete. Please try the process again.');
      // navigate('/login'); // Or to a more appropriate error page/dashboard
      return;
    }
    setLinkingToken(token);
    setProvider(prov);
    setExistingEmail(email);
    setOauthName(name);
  }, [location]);

  const handleConfirmLink = async () => {
    if (!linkingToken) {
      setError('Linking token is missing. Cannot proceed.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await api.post('/auth/confirm-link', { linkingToken });
      if (response.data.success && response.data.accessToken && response.data.user) {
        // The backend's confirm-link now returns tokens and user object
        const backendUser = response.data.user;
        const appUser: User = {
          id: backendUser.id,
          email: backendUser.email,
          username: backendUser.username,
          role: backendUser.role,
          name: backendUser.name,
          googleId: backendUser.googleId,
          twitchId: backendUser.twitchId,
          profileImageUrl: backendUser.profileImageUrl,
        };
        await processOAuthLogin();
        setSuccessMessage(response.data.message || 'Accounts linked successfully! Redirecting...');
        setTimeout(() => {
          navigate(`/account-settings?link_success=${provider}`);
        }, 2000);
      } else {
        setError(response.data.message || 'Failed to link accounts. The link might be invalid or expired.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'An unexpected error occurred while linking accounts.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/account-settings'); // Or to dashboard/home
  };

  if (error && !linkingToken) { // Initial error if token is missing
     return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-2xl font-bold mb-4 text-red-600">Account Linking Failed</h1>
        <p className="text-red-500">{error}</p>
        <button
          onClick={() => navigate('/login')}
          className="mt-6 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Go to Login
        </button>
      </div>
    );
  }
  
  const providerName = provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : "Social Media";

  return (
    <div className="container mx-auto px-4 py-12 flex flex-col items-center text-center">
      <h1 className="text-3xl font-bold mb-6">Link Your Accounts</h1>
      {successMessage ? (
        <div className="p-4 mb-4 text-lg text-green-700 bg-green-100 rounded-md border border-green-400">
          {successMessage}
        </div>
      ) : (
        <>
          <p className="mb-4 text-lg">
            You are about to link your {providerName} account
            {oauthName ? ` (signed in as ${oauthName})` : ''}
            to your existing Clipoid account associated with <strong className="font-semibold">{existingEmail}</strong>.
          </p>
          <p className="mb-8 text-muted-foreground">
            Do you want to proceed?
          </p>

          {error && <p className="text-red-500 mb-4">{error}</p>}

          <div className="space-y-4 md:space-y-0 md:space-x-4 flex flex-col md:flex-row">
            <button
              onClick={handleConfirmLink}
              disabled={isLoading || !linkingToken}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 w-full md:w-auto"
            >
              {isLoading ? 'Linking...' : `Yes, Link ${providerName} Account`}
            </button>
            <button
              onClick={handleCancel}
              disabled={isLoading}
              className="px-6 py-3 border border-border text-foreground bg-transparent rounded-md hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 w-full md:w-auto"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LinkAccountPage;